import { z } from 'zod';

import type { BadgeProps } from '@/shared/ui';

/**
 * Quote status enum — backend integer values.
 *
 * Mirrors the legacy `QuoteStatus` enum in `src/types/quotes.ts`. Exposed as a
 * const map (not a TS enum) so values can be inlined by the bundler and used
 * directly without an enum class instance.
 *
 * IMPORTANT: numeric values must match the backend exactly. Do not renumber.
 */
export const QuoteStatus = {
  Draft: 1,
  UnderReview: 2,
  Approved: 3,
  Sent: 4,
  ChangeRequested: 5,
  Awarded: 6,
  Rejected: 7,
  Expired: 8,
  WorkOrderGenerated: 9,
} as const;

export type QuoteStatusValue = (typeof QuoteStatus)[keyof typeof QuoteStatus];

/**
 * Display labels keyed by the integer status value. Used by the badge, the
 * Select filter, the Kanban column headers and the action dialog.
 */
export const quoteStatusLabels: Record<number, string> = {
  [QuoteStatus.Draft]: 'Draft',
  [QuoteStatus.UnderReview]: 'Under Review',
  [QuoteStatus.Approved]: 'Approved',
  [QuoteStatus.Sent]: 'Sent',
  [QuoteStatus.ChangeRequested]: 'Change Requested',
  [QuoteStatus.Awarded]: 'Awarded',
  [QuoteStatus.Rejected]: 'Rejected',
  [QuoteStatus.Expired]: 'Expired',
  [QuoteStatus.WorkOrderGenerated]: 'Work Order Generated',
};

/**
 * Stable status order for select dropdowns and the Kanban column layout.
 * The order intentionally tracks the typical workflow progression.
 */
export const quoteStatusOptions: QuoteStatusValue[] = [
  QuoteStatus.Draft,
  QuoteStatus.UnderReview,
  QuoteStatus.ChangeRequested,
  QuoteStatus.Approved,
  QuoteStatus.Sent,
  QuoteStatus.Awarded,
  QuoteStatus.Rejected,
  QuoteStatus.Expired,
  QuoteStatus.WorkOrderGenerated,
];

/**
 * Map backend status values to shared/ui Badge variants per the Wave 5 brief.
 *  - Draft              → muted (neutral, working state)
 *  - UnderReview        → secondary (pending decision)
 *  - ChangeRequested    → warning (needs attention)
 *  - Approved           → success (internal approval)
 *  - Sent               → default (primary — awaiting client)
 *  - Awarded            → success (final business win)
 *  - Rejected           → destructive
 *  - Expired            → destructive (treated as a negative terminal state)
 *  - WorkOrderGenerated → success (with a "Converted" sublabel in the UI)
 */
export const quoteStatusBadgeVariant: Record<
  number,
  NonNullable<BadgeProps['variant']>
> = {
  [QuoteStatus.Draft]: 'muted',
  [QuoteStatus.UnderReview]: 'secondary',
  [QuoteStatus.ChangeRequested]: 'warning',
  [QuoteStatus.Approved]: 'success',
  [QuoteStatus.Sent]: 'default',
  [QuoteStatus.Awarded]: 'success',
  [QuoteStatus.Rejected]: 'destructive',
  [QuoteStatus.Expired]: 'destructive',
  [QuoteStatus.WorkOrderGenerated]: 'success',
};

/**
 * Accent colour per status — used for the Kanban column header bar and the
 * left-border accent on Kanban cards. Sourced from the legacy palette in
 * `src/pages/Quotes/components/QuoteStatusBadge.tsx` for visual continuity.
 */
export const quoteStatusAccent: Record<number, string> = {
  [QuoteStatus.Draft]: '#3F51B5',
  [QuoteStatus.UnderReview]: '#B26A00',
  [QuoteStatus.ChangeRequested]: '#C2410C',
  [QuoteStatus.Approved]: '#00b4d8',
  [QuoteStatus.Sent]: '#5B4DFF',
  [QuoteStatus.Awarded]: '#1F9254',
  [QuoteStatus.Rejected]: '#D32F2F',
  [QuoteStatus.Expired]: '#6B7280',
  [QuoteStatus.WorkOrderGenerated]: '#1F9254',
};

/**
 * Zod literal-union for the QuoteStatus integer values.
 *
 * Using `z.union([z.literal(1), ...])` (rather than `z.number().refine(...)`)
 * keeps `z.input` and `z.output` identical — avoids the well-known
 * react-hook-form Resolver mismatch (see MIGRATION_PLAYBOOK §3.5).
 */
export const quoteStatusSchema = z.union([
  z.literal(QuoteStatus.Draft),
  z.literal(QuoteStatus.UnderReview),
  z.literal(QuoteStatus.Approved),
  z.literal(QuoteStatus.Sent),
  z.literal(QuoteStatus.ChangeRequested),
  z.literal(QuoteStatus.Awarded),
  z.literal(QuoteStatus.Rejected),
  z.literal(QuoteStatus.Expired),
  z.literal(QuoteStatus.WorkOrderGenerated),
]);

/**
 * Kanban column id used for drag-and-drop droppables. Multiple statuses may
 * share a column (e.g. Draft + ChangeRequested both live in the "Draft" lane)
 * to match the legacy `kanbanColumns.ts` layout.
 */
export type KanbanColumnId =
  | 'draft'
  | 'review'
  | 'approved'
  | 'sent'
  | 'awarded'
  | 'rejected'
  | 'expired';

export interface QuoteKanbanColumnDef {
  id: KanbanColumnId;
  label: string;
  /** All statuses that should appear in this column. */
  statuses: QuoteStatusValue[];
  /** The single status applied when a card is dropped into this column. */
  targetStatus: QuoteStatusValue;
  /** Header accent colour. */
  accent: string;
}

/**
 * Kanban column layout — mirrors the legacy
 * `src/pages/Quotes/components/kanban/kanbanColumns.ts`. The Awarded column
 * groups both `Awarded` and `WorkOrderGenerated` since `WorkOrderGenerated` is
 * a downstream state of `Awarded` that the user reaches via the conversion
 * flow, not via drag-and-drop.
 */
export const quoteKanbanColumns: QuoteKanbanColumnDef[] = [
  {
    id: 'draft',
    label: 'Draft',
    statuses: [QuoteStatus.Draft, QuoteStatus.ChangeRequested],
    targetStatus: QuoteStatus.Draft,
    accent: quoteStatusAccent[QuoteStatus.Draft],
  },
  {
    id: 'review',
    label: 'In Review',
    statuses: [QuoteStatus.UnderReview],
    targetStatus: QuoteStatus.UnderReview,
    accent: quoteStatusAccent[QuoteStatus.UnderReview],
  },
  {
    id: 'approved',
    label: 'Approved',
    statuses: [QuoteStatus.Approved],
    targetStatus: QuoteStatus.Approved,
    accent: quoteStatusAccent[QuoteStatus.Approved],
  },
  {
    id: 'sent',
    label: 'Sent',
    statuses: [QuoteStatus.Sent],
    targetStatus: QuoteStatus.Sent,
    accent: quoteStatusAccent[QuoteStatus.Sent],
  },
  {
    id: 'awarded',
    label: 'Awarded',
    statuses: [QuoteStatus.Awarded, QuoteStatus.WorkOrderGenerated],
    targetStatus: QuoteStatus.Awarded,
    accent: quoteStatusAccent[QuoteStatus.Awarded],
  },
  {
    id: 'rejected',
    label: 'Rejected',
    statuses: [QuoteStatus.Rejected],
    targetStatus: QuoteStatus.Rejected,
    accent: quoteStatusAccent[QuoteStatus.Rejected],
  },
  {
    id: 'expired',
    label: 'Expired',
    statuses: [QuoteStatus.Expired],
    targetStatus: QuoteStatus.Expired,
    accent: quoteStatusAccent[QuoteStatus.Expired],
  },
];

export function getColumnForStatus(
  status: QuoteStatusValue | number | undefined,
): QuoteKanbanColumnDef | null {
  if (status === undefined || status === null) return null;
  return (
    quoteKanbanColumns.find((col) => col.statuses.includes(status as QuoteStatusValue)) ?? null
  );
}

export function getColumnById(
  id: string,
): QuoteKanbanColumnDef | null {
  return quoteKanbanColumns.find((col) => col.id === id) ?? null;
}

/**
 * Quote editability — used to gate Edit and Delete actions.
 */
export function isQuoteEditable(
  status: QuoteStatusValue | number | undefined,
): boolean {
  return (
    status === QuoteStatus.Draft || status === QuoteStatus.ChangeRequested
  );
}

/**
 * Status changes that require the user to enter a comment.
 *
 * Approve, RequestChanges and Reject all expect reviewer remarks — the dialog
 * surfaces a textarea + validation in these cases.
 */
export function statusChangeRequiresComment(
  status: QuoteStatusValue | number,
): boolean {
  return (
    status === QuoteStatus.Approved ||
    status === QuoteStatus.ChangeRequested ||
    status === QuoteStatus.Rejected
  );
}

/**
 * Drag-and-drop validation — restricts which target statuses are reachable
 * from the current one. Returns `null` if the move is permitted, otherwise a
 * human-readable rejection reason that the UI can surface as a toast.
 *
 * Rules mirror the legacy `QuoteKanbanBoard.validateStatusTransition`:
 *  - same status is a no-op (caller handles)
 *  - `WorkOrderGenerated` is only reachable via the explicit
 *    "Convert to Work Order" flow, never drag-and-drop
 *  - from `WorkOrderGenerated` only the reverse to `Awarded` is allowed
 *  - status pairs not in the action map are rejected
 */
const statusToAllowedSources: Partial<Record<QuoteStatusValue, QuoteStatusValue[]>> = {
  [QuoteStatus.Draft]: [QuoteStatus.ChangeRequested, QuoteStatus.Expired],
  [QuoteStatus.UnderReview]: [
    QuoteStatus.Draft,
    QuoteStatus.ChangeRequested,
    QuoteStatus.Rejected,
  ],
  [QuoteStatus.ChangeRequested]: [QuoteStatus.UnderReview],
  [QuoteStatus.Approved]: [QuoteStatus.UnderReview],
  [QuoteStatus.Sent]: [QuoteStatus.Approved],
  [QuoteStatus.Awarded]: [
    QuoteStatus.Approved,
    QuoteStatus.Sent,
    QuoteStatus.WorkOrderGenerated,
  ],
  [QuoteStatus.Rejected]: [
    QuoteStatus.Draft,
    QuoteStatus.UnderReview,
    QuoteStatus.ChangeRequested,
    QuoteStatus.Approved,
    QuoteStatus.Sent,
  ],
  [QuoteStatus.Expired]: [QuoteStatus.Sent, QuoteStatus.Approved],
};

export function validateKanbanTransition(
  current: QuoteStatusValue | number,
  target: QuoteStatusValue | number,
): string | null {
  if (current === target) return null;
  if (target === QuoteStatus.WorkOrderGenerated) {
    return 'Use "Convert to Work Order" to generate a work order — drag-and-drop is not supported for this status.';
  }
  if (
    current === QuoteStatus.WorkOrderGenerated &&
    target !== QuoteStatus.Awarded
  ) {
    return 'A quote with a work order can only be reverted back to Awarded.';
  }
  const allowedSources = statusToAllowedSources[target as QuoteStatusValue];
  if (!allowedSources || !allowedSources.includes(current as QuoteStatusValue)) {
    const fromLabel = quoteStatusLabels[current as number] ?? 'this status';
    const toLabel = quoteStatusLabels[target as number] ?? 'that status';
    return `Cannot move a quote from ${fromLabel} to ${toLabel}.`;
  }
  return null;
}
